import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Loader from "../../components/loader";
import DOCUMENTS from "../../api/services/DOCUMENTS";

/**
 * "חובות" tab — lists DocumentDebt rows for the current business.
 *
 * Two debt origins:
 *   - paired with a 320 חשבונית מס (has taxDocumentNumber + linked id)
 *   - "no document" ledger-only entry
 *
 * Per-row actions: open the linked PDF (paired only), mark paid.
 */

const STATUS_LABELS = {
  UNPAID: "פתוח",
  PAID: "שולם",
  CANCELLED: "בוטל",
};
const STATUS_COLORS = {
  UNPAID: "#F59E0B",
  PAID: "#10B981",
  CANCELLED: "#9CA3AF",
};

const S = {
  page: {
    padding: 24,
    direction: "rtl",
    fontFamily: "Heebo, Arial, sans-serif",
    maxWidth: 1200,
    margin: "0 auto",
  },
  header: { marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 800, color: "#111827", margin: 0 },
  subtitle: { fontSize: 14, color: "#6B7280", marginTop: 4 },

  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 12,
    marginBottom: 24,
  },
  statCard: {
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: 16,
  },
  statLabel: { fontSize: 12, color: "#6B7280", marginBottom: 6 },
  statValue: { fontSize: 22, fontWeight: 800, color: "#111827" },

  filters: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  select: {
    padding: "8px 12px",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    fontSize: 13,
    background: "#fff",
  },
  refreshBtn: {
    background: "#F3F4F6",
    color: "#111827",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 13,
    cursor: "pointer",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    background: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #E5E7EB",
  },
  th: {
    padding: 12,
    textAlign: "right",
    fontSize: 12,
    fontWeight: 700,
    color: "#6B7280",
    background: "#F9FAFB",
    borderBottom: "1px solid #E5E7EB",
  },
  td: {
    padding: 12,
    textAlign: "right",
    fontSize: 14,
    color: "#111827",
    borderBottom: "1px solid #F3F4F6",
  },
  linkBtn: {
    background: "#F3F4F6",
    color: "#111827",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 12,
    cursor: "pointer",
    marginInlineStart: 6,
  },
  payBtn: {
    background: "#10B981",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    marginInlineStart: 6,
  },
  badge: (color) => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 700,
    color: "#fff",
    background: color,
  }),
  emptyState: { padding: 60, textAlign: "center", color: "#6B7280" },
  noDocChip: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 700,
    color: "#6B7280",
    background: "#F3F4F6",
  },
};

const fmt = (n) =>
  "₪" +
  (Number(n) || 0).toLocaleString("he-IL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtDate = (d) => new Date(d).toLocaleDateString("he-IL");

const DocumentsDebts = () => {
  const [list, setList] = useState(null);
  const [totals, setTotals] = useState({ count: 0, sumOpen: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [acting, setActing] = useState(null); // debt id currently being mutated

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await DOCUMENTS.list_debts({ status, limit: 50 });
      setList(res.items || []);
      setTotals(res.totals || { count: 0, sumOpen: 0 });
    } catch (e) {
      toast.error("טעינה נכשלה: " + (e?.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleViewDoc = async (debt) => {
    if (!debt.taxDocumentId) return;
    try {
      const res = await DOCUMENTS.get_pdf(debt.taxDocumentId);
      if (res?.url) window.open(res.url, "_blank", "noopener");
      else toast.error("לא נמצא קובץ PDF");
    } catch (e) {
      toast.error(e?.response?.data?.message || "לא ניתן להציג את המסמך");
    }
  };

  const handleMarkPaid = async (debt) => {
    if (!window.confirm("לסמן את החוב כשולם?")) return;
    setActing(debt._id);
    try {
      await DOCUMENTS.mark_debt_paid(debt._id);
      toast.success("החוב סומן כשולם");
      fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.message || "סימון נכשל");
    }
    setActing(null);
  };

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.title}>חובות</h1>
        <div style={S.subtitle}>
          חובות שנשמרו דרך הקופה (כולל "שמירה בחוב" ללא מסמך וחשבוניות מס פתוחות
          לתשלום)
        </div>
      </div>

      {/* Stats */}
      <div style={S.statsRow}>
        <div style={S.statCard}>
          <div style={S.statLabel}>סה״כ רשומות (לפי הסינון)</div>
          <div style={S.statValue}>{totals.count}</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statLabel}>סכום פתוח לתשלום</div>
          <div style={S.statValue}>{fmt(totals.sumOpen)}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={S.filters}>
        <select
          style={S.select}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">כל הסטטוסים</option>
          <option value="UNPAID">פתוח</option>
          <option value="PAID">שולם</option>
          <option value="CANCELLED">בוטל</option>
        </select>
        <button style={S.refreshBtn} onClick={fetchList}>
          רענן
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <Loader />
      ) : !list || list.length === 0 ? (
        <div style={{ ...S.table, padding: 0 }}>
          <div style={S.emptyState}>אין חובות עדיין.</div>
        </div>
      ) : (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>תאריך</th>
              <th style={S.th}>לקוח</th>
              <th style={S.th}>סה״כ</th>
              <th style={S.th}>חשבונית מקושרת</th>
              <th style={S.th}>סטטוס</th>
              <th style={S.th}>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {list.map((d) => {
              const displayName = d.customer?.invoiceName || d.customer?.name || "—";
              return (
                <tr key={d._id}>
                  <td style={S.td}>{fmtDate(d.createdAt)}</td>
                  <td style={S.td}>{displayName}</td>
                  <td style={S.td}>{fmt(d.amount)}</td>
                  <td style={S.td}>
                    {d.taxDocumentNumber ? (
                      <button
                        style={S.linkBtn}
                        onClick={() => handleViewDoc(d)}
                        title="פתח את החשבונית המקושרת"
                      >
                        #{d.taxDocumentNumber} צפה במסמך
                      </button>
                    ) : (
                      <span style={S.noDocChip}>ללא מסמך</span>
                    )}
                  </td>
                  <td style={S.td}>
                    <span style={S.badge(STATUS_COLORS[d.status] || "#9CA3AF")}>
                      {STATUS_LABELS[d.status] || d.status}
                    </span>
                  </td>
                  <td style={S.td}>
                    {d.status === "UNPAID" && (
                      <button
                        style={{
                          ...S.payBtn,
                          opacity: acting === d._id ? 0.6 : 1,
                          cursor: acting === d._id ? "not-allowed" : "pointer",
                        }}
                        disabled={acting === d._id}
                        onClick={() => handleMarkPaid(d)}
                      >
                        סמן כשולם
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default DocumentsDebts;
